import { Plain } from './Assertions.ts'

export interface Ok<T> {
  ok: T
}

export interface Error {
  error: string
}

/**
 * Returns an ok response or an error response corresponding
 * to the standard ok or error return value.
 *
 * If the error string starts with a space-separated status code,
 * then that is used as the status code for the response.
 *
 * For example:
 *
 * {error: 'Ordinary Error'}
 *   is sent as is with status 200.
 * {error: '401 Authentication Error'}
 *   is sent as {error: 'Authentication Error'} with status 401
 */
export function response<T>(rvalue: Ok<T> | Error, extraHeaders?: Plain) {

  if ('ok' in rvalue) {
    const body = JSON.stringify(rvalue)
    return new Response(body, {
      status: 200,
      headers: {
        ...extraHeaders,
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      }
    })
  }

  const error = rvalue.error
  const [, status, message] = error.match(/^(\d{3})\s(.+)/) ?? [null, '200', error]
  const body = JSON.stringify({
    error: message
  })
  return new Response(body, {
    status: Number(status),
    headers: {
      ...extraHeaders,
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*'
    }
  })
}

/**
 * Returns a 404 error response, including
 * the standard JSON error return value.
 * @deprecated Use response instead using a status-prefixed error message.
 */
export function response404<T>(json: Error = {
  error: '404 Not Found'
}) {
  const body = JSON.stringify(json)
  return new Response(body, {
    status: 404,
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': '*',
    }
  })
}

/**
 * Returns a 302 permanent redirect response.
 * @param url the url to redirect to.
 * @returns Response object.
 */
export function response302(url: URL, extraHeaders: Plain = []): Response {
  return new Response(null, {
    status: 302,
    headers: {
      'Location': url.toString(),
      'Access-Control-Allow-Origin': '*',
      ...extraHeaders
    }
  })
}

/**
 * Simple build-a-cookie support.
 *
 * If the `expires` attribute is not defined, it's a session cookie.
 * Otherwise it's a permanent cookie. To delete a cookie, use the
 * `expires` attribute set to `new Date(0)`.
 */

export interface CookiePayload {
  name: string
  value: string
  domain?: string
  sameSite?: 'Strict' | 'Lax' | 'None'
  path?: string
  expires?: Date
  secure?: boolean
  httpOnly?: boolean
}

export type CookieString = string

export function buildCookie(payload: CookiePayload): CookieString {
  const {
    name,
    value,
    domain,
    sameSite,
    path = '/',
    expires,
    secure = true,
    httpOnly = true
  } = payload

  const builder: string[] = []
  builder.push(`${encodeURIComponent(name)}=${encodeURIComponent(value)}`)
  if (domain) builder.push(`Domain=${domain}`)
  if (sameSite) builder.push(`SameSite=${sameSite}`)
  builder.push(`Path=${path}`)
  if (expires) builder.push(`Expires=${expires.toUTCString()}`)
  if (secure) builder.push(`Secure`)
  if (httpOnly) builder.push('HttpOnly')

  const cookie = builder.join('; ')
  return cookie
}
